import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import {
  webhookService,
  type CreateWebhookData,
  type WebhookEndpoint,
} from '@services/webhookService';
import { visuallyHidden } from '@mui/utils';
import { toDisplayError } from '../utils/apiError';
import { useActiveOrgStore } from '@store/index';
import SecretRevealDialog from '@components/webhooks/SecretRevealDialog';
import WebhookFormDialog from '@components/webhooks/WebhookFormDialog';
import DeliveryLogDialog from '@components/webhooks/DeliveryLogDialog';
import { deliveryErrorText } from '@utils/webhookDeliveryError';

/**
 * Webhook endpoint management (REV-442).
 *
 * Everything here is org-scoped; the API refuses another organization's
 * endpoint with a 404 regardless, so the page never has to police tenancy
 * itself.
 *
 * Two things this page is careful about, both because getting them wrong is
 * unrecoverable rather than merely annoying:
 *
 * - The signing secret is shown ONCE. SecretRevealDialog exists to make that
 *   moment hard to miss and hard to dismiss by accident.
 * - Rotation invalidates the secret a live receiver may be using, so it is
 *   behind a confirmation that says what will break.
 */
export default function WebhooksPage() {
  const { t } = useTranslation();
  const activeOrg = useActiveOrgStore((state) => state.activeOrg);
  const orgId = activeOrg?.id;

  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<WebhookEndpoint | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [secret, setSecret] = useState<string | null>(null);
  const [secretVariant, setSecretVariant] = useState<'created' | 'rotated'>('created');

  const [confirming, setConfirming] = useState<{
    kind: 'rotate' | 'delete';
    endpoint: WebhookEndpoint;
  } | null>(null);

  const [logFor, setLogFor] = useState<WebhookEndpoint | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setLoadError(null);
    try {
      setEndpoints(await webhookService.list(orgId));
    } catch {
      setLoadError(t('webhooks.loadError'));
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = async (data: CreateWebhookData) => {
    if (!orgId) return;
    setSubmitting(true);
    setFormError(null);
    try {
      if (editing) {
        await webhookService.update(orgId, editing.id, data);
      } else {
        const created = await webhookService.create(orgId, data);
        setSecretVariant('created');
        setSecret(created.secret);
      }
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (error) {
      // Re-enable the button and say why. The most likely cause is a URL that
      // resolves somewhere internal, which only the server can determine.
      setFormError(toDisplayError(error, t('common.error')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleTest = async (endpoint: WebhookEndpoint) => {
    if (!orgId) return;
    setTestingId(endpoint.id);
    setTestResult(null);
    try {
      const response = await webhookService.sendTest(orgId, endpoint.id);
      const delivered = response.data.status === 'delivered';
      setTestResult({
        ok: delivered,
        message: delivered
          ? t('webhooks.test.success')
          : `${t('webhooks.test.failure')} ${deliveryErrorText(t, response.data) ?? ''}`.trim(),
      });
      await load();
    } catch (error) {
      setTestResult({ ok: false, message: toDisplayError(error, t('common.error')) });
    } finally {
      setTestingId(null);
    }
  };

  const handleConfirm = async () => {
    if (!orgId || !confirming) return;
    const { kind, endpoint } = confirming;
    setConfirming(null);
    try {
      if (kind === 'rotate') {
        const rotated = await webhookService.rotateSecret(orgId, endpoint.id);
        setSecretVariant('rotated');
        setSecret(rotated.secret);
      } else {
        await webhookService.remove(orgId, endpoint.id);
      }
      await load();
    } catch (error) {
      setLoadError(toDisplayError(error, t('common.error')));
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 3, gap: 2 }}
      >
        <Box>
          <Typography variant="h1" sx={{ fontSize: '2rem', mb: 1 }}>
            {t('webhooks.title')}
          </Typography>
          <Typography color="text.secondary">{t('webhooks.subtitle')}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null);
            setFormError(null);
            setFormOpen(true);
          }}
        >
          {t('webhooks.add')}
        </Button>
      </Stack>

      {/* Both announce themselves: MUI's Alert carries role="alert" by default,
          and an explicit role="status" makes the test result POLITE — it
          reports the outcome of something the user just did, and interrupting
          them mid-sentence to say "delivered" is worse than waiting. Wrapping
          either in another live region would nest two and announce twice. */}
      {testResult && (
        <Alert role="status" severity={testResult.ok ? 'success' : 'warning'} sx={{ mb: 2 }}>
          {testResult.message}
        </Alert>
      )}
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label={t('common.loading')} />
        </Box>
      ) : endpoints.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h2" sx={{ fontSize: '1.25rem', mb: 1 }}>
            {t('webhooks.empty')}
          </Typography>
          <Typography color="text.secondary">{t('webhooks.emptyHelp')}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <caption style={visuallyHidden}>{t('webhooks.subtitle')}</caption>
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="col">
                  {t('webhooks.columns.name')}
                </TableCell>
                <TableCell component="th" scope="col">
                  {t('webhooks.columns.url')}
                </TableCell>
                <TableCell component="th" scope="col">
                  {t('webhooks.columns.events')}
                </TableCell>
                <TableCell component="th" scope="col">
                  {t('webhooks.columns.status')}
                </TableCell>
                <TableCell component="th" scope="col">
                  {t('webhooks.columns.secret')}
                </TableCell>
                <TableCell component="th" scope="col">
                  {t('webhooks.columns.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {endpoints.map((endpoint) => (
                <TableRow key={endpoint.id}>
                  <TableCell component="th" scope="row">
                    {endpoint.name}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-all' }}>{endpoint.url}</TableCell>
                  <TableCell>
                    {endpoint.eventTypes.length === 0
                      ? t('webhooks.noEvents')
                      : t('webhooks.eventCount', { count: endpoint.eventTypes.length })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`webhooks.status.${endpoint.status}`)}
                      color={endpoint.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {endpoint.secretLast4
                      ? t('webhooks.secretEndsWith', { last4: endpoint.secretLast4 })
                      : t('webhooks.secretNever')}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {/* Every icon button carries the endpoint NAME in its
                          accessible name. "Edit" repeated six times down a
                          table tells a screen reader user nothing about which
                          row they are on.

                          `describeChild` is not cosmetic: without it MUI adds
                          an aria-label to the Tooltip's direct child, and the
                          <span> a disabled-capable button needs is role
                          `generic`, where ARIA PROHIBITS naming attributes
                          (ARIA-40, caught by our own engine). The button
                          already has a name; the tooltip is a description. */}
                      <Tooltip
                        describeChild
                        title={t('webhooks.actions.edit', { name: endpoint.name })}
                      >
                        <IconButton
                          aria-label={t('webhooks.actions.edit', { name: endpoint.name })}
                          onClick={() => {
                            setEditing(endpoint);
                            setFormError(null);
                            setFormOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        describeChild
                        title={t('webhooks.actions.test', { name: endpoint.name })}
                      >
                        <span>
                          <IconButton
                            aria-label={t('webhooks.actions.test', { name: endpoint.name })}
                            onClick={() => void handleTest(endpoint)}
                            disabled={testingId === endpoint.id}
                          >
                            {testingId === endpoint.id ? (
                              <CircularProgress size={20} aria-label={t('webhooks.test.sending')} />
                            ) : (
                              <SendIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        describeChild
                        title={t('webhooks.actions.deliveries', { name: endpoint.name })}
                      >
                        <IconButton
                          aria-label={t('webhooks.actions.deliveries', { name: endpoint.name })}
                          onClick={() => setLogFor(endpoint)}
                        >
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        describeChild
                        title={t('webhooks.actions.rotate', { name: endpoint.name })}
                      >
                        <IconButton
                          aria-label={t('webhooks.actions.rotate', { name: endpoint.name })}
                          onClick={() => setConfirming({ kind: 'rotate', endpoint })}
                        >
                          <AutorenewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        describeChild
                        title={t('webhooks.actions.delete', { name: endpoint.name })}
                      >
                        <IconButton
                          aria-label={t('webhooks.actions.delete', { name: endpoint.name })}
                          onClick={() => setConfirming({ kind: 'delete', endpoint })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <WebhookFormDialog
        open={formOpen}
        endpoint={editing}
        submitting={submitting}
        error={formError}
        onSubmit={(data) => void handleSubmit(data)}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
      />

      <SecretRevealDialog
        open={Boolean(secret)}
        secret={secret}
        variant={secretVariant}
        onClose={() => setSecret(null)}
      />

      <DeliveryLogDialog
        open={Boolean(logFor)}
        orgId={orgId ?? null}
        endpoint={logFor}
        onClose={() => setLogFor(null)}
      />

      <Dialog
        open={Boolean(confirming)}
        onClose={() => setConfirming(null)}
        aria-labelledby="webhook-confirm-title"
        aria-describedby="webhook-confirm-body"
      >
        <DialogTitle id="webhook-confirm-title">
          {confirming?.kind === 'rotate'
            ? t('webhooks.rotate.confirmTitle')
            : t('webhooks.delete.confirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="webhook-confirm-body">
            {confirming?.kind === 'rotate'
              ? t('webhooks.rotate.confirmBody')
              : t('webhooks.delete.confirmBody', { name: confirming?.endpoint.name ?? '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setConfirming(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            variant="contained"
            color={confirming?.kind === 'delete' ? 'error' : 'primary'}
          >
            {confirming?.kind === 'rotate'
              ? t('webhooks.rotate.confirm')
              : t('webhooks.delete.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
